import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, ExternalLink, Loader2, MonitorPlay, Image as ImageIcon, Monitor, Smartphone } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import CanvasEditor from "@/components/app/CanvasEditor";
import { configToElements } from "@/components/app/canvasModel";

export default function MenuEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [name, setName] = useState("");
  const [elements, setElements] = useState([]);
  const [background, setBackground] = useState("#FFFFFF");
  const [canvasW, setCanvasW] = useState(1920);
  const [canvasH, setCanvasH] = useState(1080);
  const [saving, setSaving] = useState(false);
  const dirty = useRef(false);
  const [screens, setScreens] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get(`/designs/${id}`),
      api.get(`/screens`).catch(() => ({ data: { screens: [] } })),
    ]).then(([r, s]) => {
      setDesign(r.data);
      setName(r.data.name || "Untitled design");
      const cfg = r.data.config || {};
      const els = configToElements(cfg);
      setElements(els);
      setBackground(cfg.background_color || "#FFFFFF");
      setCanvasW(cfg.canvas_w || 1920);
      setCanvasH(cfg.canvas_h || 1080);
      setScreens(s.data.screens || []);
    }).catch((err) => {
      toast.error(formatApiError(err));
      navigate("/dashboard/designs");
    });
  }, [id, navigate]);

  const onElementsChange = (next) => {
    setElements(next);
    dirty.current = true;
  };

  const flipOrientation = () => {
    // Rotate canvas 90° and remap element coords/sizes so the design still fits.
    // For a comfortable read after flipping we also scale element widths and
    // font sizes proportionally to the new canvas — otherwise landscape→portrait
    // flips leave wide text elements clipped at the new narrower edges.
    const oldW = canvasW, oldH = canvasH;
    const newW = oldH, newH = oldW;
    const rx = newW / oldW;   // horizontal scale (< 1 when going wide → tall)
    const ry = newH / oldH;   // vertical scale   (> 1 when going wide → tall)
    const s = Math.min(rx, ry); // uniform font-size scale — never blow up text
    const remapped = elements.map((el) => {
      // Coord swap keeps top-left → new bottom-left, so we rotate visually
      // through a coord-swap remap identical to the previous version…
      const nxRaw = (el.y ?? 0) * (newW / oldH);
      const nyRaw = (oldW - (el.x ?? 0) - (el.w ?? 0)) * (newH / oldW);
      // …then also uniformly rescale element box + font so it comfortably
      // fits inside the new canvas.
      const nw = Math.max(4, Math.round((el.h ?? 0) * (newW / oldH) * (rx / ry || 1)));
      const nh = Math.max(4, Math.round((el.w ?? 0) * (newH / oldW) * (ry / rx || 1)));
      const next = {
        ...el,
        x: Math.round(nxRaw),
        y: Math.round(nyRaw),
        w: nw,
        h: nh,
      };
      if (el.kind === "text" && typeof el.fontSize === "number") {
        // Scale font gently — keep at least 12px so nothing becomes unreadable.
        next.fontSize = Math.max(12, Math.round(el.fontSize * s));
      }
      // Clamp inside canvas so nothing overflows.
      next.x = Math.max(0, Math.min(newW - 4, next.x));
      next.y = Math.max(0, Math.min(newH - 4, next.y));
      next.w = Math.min(nw, newW - next.x);
      next.h = Math.min(nh, newH - next.y);
      return next;
    });
    setCanvasW(newW);
    setCanvasH(newH);
    setElements(remapped);
    dirty.current = true;
  };

  const canvaSource = design?.config?.canva_source;

  const save = async () => {
    if (!design) return;
    setSaving(true);
    try {
      const currentConfig = design.config || {};
      const nextConfig = { ...currentConfig, elements, background_color: background, canvas_w: canvasW, canvas_h: canvasH };
      const r = await api.put(`/designs/${id}`, { name, config: nextConfig });
      setDesign(r.data);
      dirty.current = false;
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // Auto-save on Cmd/Ctrl+S
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [save]);

  const pushToScreen = async (screenId) => {
    try {
      // Save first
      if (dirty.current) await save();
      await api.put(`/screens/${screenId}`, { active_design_id: id });
      toast.success("Pushed to screen");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!design) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
          Loading design…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col h-screen">
        {/* Top header */}
        <header className="border-b border-slate-200 bg-white px-4 py-2 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="rounded-full text-slate-700 hover:bg-slate-100" onClick={() => navigate("/dashboard/designs")} data-testid="editor-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Input
              value={name}
              onChange={(e) => { setName(e.target.value); dirty.current = true; }}
              className="border-0 bg-transparent text-base font-semibold text-slate-900 focus-visible:ring-1 focus-visible:ring-sky-500 max-w-xs"
              data-testid="editor-name-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <OrientationToggle canvasW={canvasW} canvasH={canvasH} onFlip={flipOrientation} />
            <BgColorPicker value={background} onChange={(c) => { setBackground(c); dirty.current = true; }} />

            {canvaSource?.edit_url && (
              <a href={canvaSource.edit_url} target="_blank" rel="noreferrer" className="hidden md:inline-flex">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="edit-in-canva-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />Edit in Canva
                </Button>
              </a>
            )}

            <a href={`/preview/${id}`} target="_blank" rel="noreferrer" className="hidden sm:inline-flex">
              <Button variant="outline" size="sm" className="rounded-full bg-white border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="editor-preview">
                <ExternalLink className="h-4 w-4 mr-1" />Preview
              </Button>
            </a>
            <PushToScreenButton screens={screens} onPush={pushToScreen} />
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm font-semibold px-4 h-8"
              data-testid="editor-save"
            >
              <Save className="h-4 w-4 mr-1" />{saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </header>

        {/* Canvas editor — every layout decomposes into editable elements */}
        <div className="flex-1 min-h-0">
          <CanvasEditor
            elements={elements}
            onChange={onElementsChange}
            background={background}
            designTitle={name}
            canvasW={canvasW}
            canvasH={canvasH}
            onPushToScreen={undefined /* handled in header now */}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}

function OrientationToggle({ canvasW, canvasH, onFlip }) {
  const isPortrait = canvasH > canvasW;
  return (
    <button
      onClick={onFlip}
      className="h-8 px-3 inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold text-slate-700"
      data-testid="orientation-toggle"
      title={isPortrait ? "Flip to landscape 16:9" : "Flip to portrait 9:16"}
    >
      {isPortrait ? <Smartphone className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />}
      <span className="tabular-nums">{isPortrait ? "9:16" : "16:9"}</span>
    </button>
  );
}

function BgColorPicker({ value, onChange }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="h-8 px-2 inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-xs text-slate-700" data-testid="bg-color-btn" title="Canvas background">
          <ImageIcon className="h-3.5 w-3.5" />
          <div className="h-4 w-4 rounded border border-slate-300" style={{ background: value }} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-3 bg-white border border-slate-200" align="end">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Canvas background</div>
        <div className="grid grid-cols-6 gap-2 mb-3">
          {["#FFFFFF", "#0F172A", "#0EA5E9", "#FCE7F3", "#FEF3C7", "#DCFCE7"].map((c) => (
            <button key={c} onClick={() => onChange(c)} className="h-8 w-8 rounded-md border border-slate-200" style={{ background: c }} />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 border-0" />
          <Input value={value} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PushToScreenButton({ screens, onPush }) {
  const available = screens.filter((s) => s.user_id);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full bg-white border-sky-200 text-sky-700 hover:bg-sky-50 hidden sm:inline-flex" data-testid="push-to-screen-btn">
          <MonitorPlay className="h-4 w-4 mr-1" />Push to Screen
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2 bg-white border border-slate-200" align="end">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-2 py-1.5">Choose a screen</div>
        {available.length === 0 ? (
          <div className="text-xs text-slate-500 px-2 py-3">No paired screens. Pair one first.</div>
        ) : available.map((s) => (
          <button
            key={s.id}
            onClick={() => onPush(s.id)}
            className="w-full text-left px-2 py-2 hover:bg-slate-50 rounded-md text-sm text-slate-700 flex items-center justify-between"
            data-testid={`push-target-${s.id}`}
          >
            <span className="truncate">{s.name || "Untitled screen"}</span>
            <span className="text-[10px] text-slate-400 font-mono">{s.pairing_code || ""}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
